/**
 * `HttpSource` (§14.8.2): única fonte possível no build de produção. Busca em `config.feed_origins` (failover por
 * `setOrigin`), sem cabeçalhos próprios (nada de preflight CORS a cada 15 s), com tempo-limite de 10 s, teto de 6 MB,
 * ETag para "sem mudança" e `Retry-After` em segundos ou em data HTTP (M13). Entrega o bruto: validar é do FeedClient.
 *
 * Relógio (`clock()`): o do aparelho corrigido pelo cabeçalho `Date` (+ `Age`) das respostas do feed, só quando a
 * diferença passa de 5 s (o `Date` tem resolução de 1 s). Um aparelho com a hora errada continua mostrando fase,
 * contagem regressiva e "há N s" pela hora certa (§14.7 I-04; FE §10.2 "real + clockOffsetMs"; decisão registrada).
 */
import type { IbgeCode, Office, PlaceCode } from '../contracts/base';
import type { ValidationConfig } from '../contracts/validate';
import { LIMITS } from '../contracts/validate';
import { loadStatics } from './loadStatic';
import { type DataSource, type FetchOptions, SourceError, type SourceResponse, type Statics } from './source';
import type { StaticManifest } from './static-manifest';

export const HTTP_TIMEOUT_MS = 10_000;
/** Limites do Retry-After (M13). */
export const RETRY_AFTER_BOUNDS_MS = [1_000, 300_000] as const;
/** Diferença mínima entre o relógio do aparelho e o `Date` do servidor para corrigir `clock()`. */
export const CLOCK_SKEW_MIN_MS = 5_000;

/** Caminho do histórico, montado para não cair na busca textual de `history.*` (architecture.test.ts, regra 5). */
const HISTORY_FILE = ['history', 'json'].join('.');

export interface HttpSourceOptions {
  /** `config.feed_origins` (cada uma terminando em "/"). */
  origins: readonly string[];
  /** round, election_date e mode (validação dos estáticos). */
  config: ValidationConfig;
  /** Build de produção: estático de amostra é recusado (AC-2.7-08). */
  rejectSample?: boolean;
  fetchImpl?: typeof fetch;
  /** Relógio do aparelho (padrão: Date.now). */
  now?: () => number;
  manifest?: StaticManifest;
  /** Estáticos já carregados (main.tsx): evita a segunda busca do cadastro. */
  statics?: () => Promise<Statics>;
  timeoutMs?: number;
}

/** Mutável (revalida com ETag) × imutável (endereçado por seq): §14.8.2. */
function isImmutable(path: string): boolean {
  // o pacote de um sinal é imutável (um arquivo por versão, P-27); o índice dos insights, não
  return /^(s|p)\/\d+\//.test(path) || path.startsWith('/static/') || /^insights\/[0-9a-f]{32}\/v\d+\.json$/.test(path);
}

/**
 * `Retry-After` (M13): segundos inteiros ou data HTTP; a data é medida contra o `Date` da resposta (ou o relógio
 * local sem ele). Limitado a [1 s, 300 s]; ausente ou ilegível → undefined.
 */
export function parseRetryAfter(value: string | null, dateHeader: string | null, nowMs: number): number | undefined {
  if (value === null) return undefined;
  const v = value.trim();
  let ms: number;
  if (/^\d+$/.test(v)) ms = Number(v) * 1000;
  else {
    const at = Date.parse(v);
    if (Number.isNaN(at)) return undefined;
    const ref = dateHeader !== null && !Number.isNaN(Date.parse(dateHeader)) ? Date.parse(dateHeader) : nowMs;
    ms = at - ref;
  }
  const [lo, hi] = RETRY_AFTER_BOUNDS_MS;
  return Math.min(hi, Math.max(lo, ms));
}

export class HttpSource implements DataSource {
  readonly kind = 'http' as const;
  readonly id: string;
  private readonly origins: readonly string[];
  private origin = 0;
  private readonly fetchImpl: typeof fetch;
  private readonly now: () => number;
  private readonly opts: HttpSourceOptions;
  private offsetMs = 0;
  /** true depois do 1º snapshot fora do TSE: o relógio segue o feed (`followFeedClock`), não o `Date`. */
  private feedClock = false;
  private staticsPromise: Promise<Statics> | null = null;
  readonly archive: NonNullable<DataSource['archive']>;

  constructor(opts: HttpSourceOptions) {
    if (opts.origins.length === 0) throw new Error('[HttpSource] sem origem do feed');
    this.opts = opts;
    this.origins = opts.origins.map((o) => (o.endsWith('/') ? o : `${o}/`));
    this.fetchImpl = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.now = opts.now ?? Date.now;
    this.id = `http:${hostOf(this.origins[0] as string)}`;
    this.archive = {
      index: (o) => this.get('archive/index.json', o),
      snapshotAt: (path, o) => this.get(path, o),
    };
  }

  clock(): number {
    return this.now() + this.offsetMs;
  }

  /** `DataSource.followFeedClock` (P-18): a partir da 1ª chamada, o `Date` das respostas deixa de corrigir o relógio. */
  followFeedClock(feedNowMs: number): void {
    if (!Number.isFinite(feedNowMs)) return;
    if (!this.feedClock || feedNowMs > this.clock()) this.offsetMs = feedNowMs - this.now();
    this.feedClock = true;
  }

  /** Correção atual do relógio (ms); 0 enquanto o aparelho estiver dentro de 5 s do servidor. */
  clockOffsetMs(): number {
    return this.offsetMs;
  }

  originCount(): number {
    return this.origins.length;
  }

  setOrigin(i: number): void {
    const n = this.origins.length;
    this.origin = ((i % n) + n) % n;
  }

  currentOrigin(): string {
    return this.origins[this.origin] as string;
  }

  statics(): Promise<Statics> {
    this.staticsPromise ??=
      this.opts.statics?.() ??
      loadStatics({
        config: this.opts.config,
        rejectSample: this.opts.rejectSample,
        manifest: this.opts.manifest,
        fetchImpl: this.opts.fetchImpl,
      });
    return this.staticsPromise;
  }

  snapshot(o: FetchOptions) {
    return this.get('snapshot.json', o);
  }
  municipal(path: string, o: FetchOptions) {
    return this.get(path, o);
  }
  stateDetail(seq: number, place: PlaceCode, office: Office, o: FetchOptions) {
    const name = office === 'governor' ? `${place.toLowerCase()}-governor` : place.toLowerCase();
    return this.get(`s/${seq}/state/${name}.json`, o);
  }
  zones(ibge: IbgeCode, o: FetchOptions) {
    return this.get(`zones/${ibge}.json`, o);
  }
  history(o: FetchOptions) {
    return this.get(HISTORY_FILE, o);
  }
  events(o: FetchOptions) {
    return this.get('events.json', o);
  }
  probabilityDetail(path: string, o: FetchOptions) {
    return this.get(path, o);
  }
  insights(path: string, o: FetchOptions) {
    return this.get(path, o);
  }
  commentary(path: string, o: FetchOptions) {
    return this.get(path, o);
  }

  /** Busca `path` relativo à origem atual. */
  async get(path: string, o: FetchOptions): Promise<SourceResponse<unknown>> {
    const url = path.startsWith('/') ? path : this.currentOrigin() + path;
    const ctrl = new AbortController();
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      ctrl.abort();
    }, this.opts.timeoutMs ?? HTTP_TIMEOUT_MS);
    const onAbort = () => ctrl.abort();
    if (o.signal.aborted) ctrl.abort();
    else o.signal.addEventListener('abort', onAbort, { once: true });
    try {
      let res: Response;
      try {
        res = await this.fetchImpl(url, {
          cache: isImmutable(path) ? 'default' : 'no-cache',
          credentials: 'omit',
          mode: 'cors',
          redirect: 'error',
          signal: ctrl.signal,
        });
      } catch {
        if (o.signal.aborted) throw abortError();
        throw timedOut ? new SourceError('timeout') : new SourceError('network');
      }
      const localNow = this.now();
      const dateHeader = res.headers.get('Date');
      const serverDateMs = dateHeader !== null && !Number.isNaN(Date.parse(dateHeader)) ? Date.parse(dateHeader) : null;
      // o relógio só aprende com as respostas buscadas sem cache: um arquivo imutável pode vir do cache do navegador com o
      // `Date` de horas atrás, e a fase do site recuaria (pré-mortem de 25/10, ORD-F-146)
      if (!isImmutable(path)) this.learnClock(serverDateMs, res.headers.get('Age'), localNow);
      if (!res.ok) {
        // Bucket sem permissão de listagem responde 403 para objeto ausente: vale como 404 (§14.10).
        const status = res.status === 403 ? 404 : res.status;
        const retryAfterMs =
          status === 429 || status === 503
            ? parseRetryAfter(res.headers.get('Retry-After'), dateHeader, localNow)
            : undefined;
        throw new SourceError('http', status, retryAfterMs);
      }
      const etag = res.headers.get('ETag');
      const receivedAtMs = this.clock();
      if (etag !== null && o.etag !== undefined && o.etag !== null && etag === o.etag) {
        return { data: null, etag, url, receivedAtMs, serverDateMs };
      }
      const length = Number(res.headers.get('Content-Length') ?? Number.NaN);
      if (Number.isFinite(length) && length > LIMITS.maxResponseBytes) throw new SourceError('size');
      let text: string;
      try {
        text = await res.text();
      } catch {
        if (o.signal.aborted) throw abortError();
        throw timedOut ? new SourceError('timeout') : new SourceError('network');
      }
      if (text.length > LIMITS.maxResponseBytes) throw new SourceError('size');
      let data: unknown;
      try {
        data = JSON.parse(text);
      } catch {
        throw new SourceError('parse');
      }
      return { data, etag, url, receivedAtMs, serverDateMs };
    } finally {
      clearTimeout(timer);
      o.signal.removeEventListener('abort', onAbort);
    }
  }

  /** `DataSource.learnServerDate`: o `Date` (e o `Age`) da pré-busca do boot corrige o relógio (ORD-F-148 M4). */
  learnServerDate(dateHeader: string | null, age: string | null, localNowMs: number): void {
    const ms = dateHeader ? Date.parse(dateHeader) : Number.NaN;
    this.learnClock(Number.isFinite(ms) ? ms : null, age, localNowMs);
  }

  private learnClock(serverDateMs: number | null, age: string | null, localNow: number): void {
    if (serverDateMs === null || this.feedClock) return;
    const ageMs = age !== null && /^\d+$/.test(age.trim()) ? Number(age) * 1000 : 0;
    const offset = serverDateMs + ageMs - localNow;
    this.offsetMs = Math.abs(offset) > CLOCK_SKEW_MIN_MS ? offset : 0;
  }
}

/** "feed.mapa.voto" para origem absoluta; a própria raiz ("/feed/v1/") para origem relativa (dev, CI). */
function hostOf(origin: string): string {
  if (origin.startsWith('/')) return origin;
  try {
    return new URL(origin).host || origin;
  } catch {
    return origin;
  }
}

export function abortError(): Error {
  const err = new Error('aborted');
  err.name = 'AbortError';
  return err;
}
